import { useEffect, useRef } from "react"

interface Agent {
  run: () => Promise<unknown>
  reattach?: () => Promise<unknown>
  running: boolean
}

interface Options {
  /** "oio" | "oio_rewrite" | future agent names — purely for the dedup key below. */
  agentName: string
  threadId: string | undefined
  /** Caller computes this from ITS OWN status vocabulary — OIO's draft_status
   * strings won't necessarily match a future draft type's. */
  isTerminal: boolean
  agent: Agent
}

/**
 * The frontend half of the reconnect fix: CopilotKit's connectAgent() cannot
 * restore state on mount for a generic AG-UI HttpAgent backend (see
 * docs/copilotkit-integration-analysis.md §1b) — it always throws
 * AGUIConnectNotImplementedError, silently swallowed. This hook instead
 * triggers an ordinary reconnect call (`agent.reattach()` when available,
 * otherwise `agent.run()`), which the backend's RunCoordinator
 * (backend/oio_service/run_coordination/) routes to either "start fresh"
 * (nothing was running) or "reattach to the buffered+live run" (something
 * was/is running) — the caller doesn't need to know which.
 *
 * Never called for a terminal thread — that path goes through
 * hydrateFinishedDraft instead (features/drafting/services/draft-hydration.service.ts),
 * which never touches the agent stream at all.
 */
export function useDraftStreamReconnect({ agentName, threadId, isTerminal, agent }: Options): void {
  const attemptedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!threadId) return
    const key = `${agentName}:${threadId}`
    if (isTerminal || agent.running || attemptedRef.current === key) return
    attemptedRef.current = key
    if (!agent.reattach && agentName === "oio_rewrite") {
      console.warn(`[copilotkit] Reconnect skipped: no reattach method for agent ${agentName}`)
      return
    }
    const reattach = agent.reattach
    const reconnect: () => Promise<unknown> = reattach ? () => reattach() : () => agent.run()
    reconnect().catch((err) => {
      console.warn(`[copilotkit] Reconnect failed for agent ${agentName}:`, err)
    })
  }, [agentName, threadId, isTerminal, agent])
}
